import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import './SupervisorEquipmentManager.css';

function SupervisorEquipmentManager({ user, onBack }) {
  const [equipment, setEquipment] = useState([]);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // all | keg_pump | draft_machine | decommissioned
  const [search, setSearch] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState(null); // for detail/edit modal
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDecommission, setConfirmDecommission] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  // ✅ FIXED: service history is now fetched on demand, scoped to one
  // piece of equipment, only when its detail view is actually opened —
  // not bulk-loaded for the entire fleet on every page load.
  useEffect(() => {
    if (!selectedEquipment) { setServiceHistory([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'serviceRecords'),
            where('serialNumber', '==', selectedEquipment.serialNumber),
            orderBy('createdAt', 'desc'),
            limit(10)
          )
        );
        if (!cancelled) setServiceHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading service history for equipment:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEquipment]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load equipment only — service history is loaded separately, on
      // demand, per the effect above. Previously this also downloaded
      // the ENTIRE serviceRecords collection just to cross-reference
      // it client-side against every equipment card in the list — at
      // real scale (tens of thousands of pumps) that made the list
      // effectively unusable, not just slow.
      const equipSnap = await getDocs(
        query(collection(db, 'equipment'), orderBy('createdAt', 'desc'))
      );
      const equipList = equipSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEquipment(equipList);
    } catch (err) {
      console.error('Error loading equipment:', err);
      alert('Failed to load equipment data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ REMOVED: getServicesForEquipment(). serviceHistory is now fetched
  // already scoped to the selected equipment (see the effect above), so
  // there's nothing left to filter — using it directly is both correct
  // and free, instead of re-scanning a bulk array on every call.

  const filteredEquipment = equipment.filter(eq => {
    const matchesSearch =
      eq.serialNumber?.toLowerCase().includes(search.toLowerCase()) ||
      eq.outletName?.toLowerCase().includes(search.toLowerCase()) ||
      eq.technicianName?.toLowerCase().includes(search.toLowerCase());

    if (activeFilter === 'all') return matchesSearch && eq.status !== 'decommissioned';
    if (activeFilter === 'decommissioned') return matchesSearch && eq.status === 'decommissioned';
    return matchesSearch && eq.equipmentType === activeFilter && eq.status !== 'decommissioned';
  });

  const counts = {
    all: equipment.filter(e => e.status !== 'decommissioned').length,
    keg_pump: equipment.filter(e => e.equipmentType === 'keg_pump' && e.status !== 'decommissioned').length,
    draft_machine: equipment.filter(e => e.equipmentType === 'draft_machine' && e.status !== 'decommissioned').length,
    decommissioned: equipment.filter(e => e.status === 'decommissioned').length
  };

  const openDetail = (eq) => {
    setSelectedEquipment(eq);
    setEditForm({
      serialNumber: eq.serialNumber,
      outletName: eq.outletName,
      installationDate: eq.installationDate,
      condition: eq.condition,
      notes: eq.notes || ''
    });
    setEditMode(false);
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      await updateDoc(doc(db, 'equipment', selectedEquipment.id), {
        serialNumber: editForm.serialNumber.trim().toUpperCase(),
        outletName: editForm.outletName.trim(),
        installationDate: editForm.installationDate,
        condition: editForm.condition,
        notes: editForm.notes.trim(),
        updatedAt: Timestamp.now(),
        updatedBy: user.name
      });

      // Refresh local state
      setEquipment(prev =>
        prev.map(e =>
          e.id === selectedEquipment.id
            ? { ...e, ...editForm, serialNumber: editForm.serialNumber.toUpperCase(), updatedAt: Timestamp.now() }
            : e
        )
      );
      setSelectedEquipment(prev => ({
        ...prev,
        ...editForm,
        serialNumber: editForm.serialNumber.toUpperCase()
      }));
      setEditMode(false);
    } catch (err) {
      console.error('Error saving edit:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDecommission = async (eq) => {
    try {
      await updateDoc(doc(db, 'equipment', eq.id), {
        status: 'decommissioned',
        decommissionedAt: Timestamp.now(),
        decommissionedBy: user.name
      });
      setEquipment(prev =>
        prev.map(e =>
          e.id === eq.id ? { ...e, status: 'decommissioned' } : e
        )
      );
      setConfirmDecommission(null);
      setSelectedEquipment(null);
    } catch (err) {
      console.error('Error decommissioning:', err);
      alert('Failed to decommission equipment. Please try again.');
    }
  };

  const handleReactivate = async (eq) => {
    try {
      await updateDoc(doc(db, 'equipment', eq.id), {
        status: 'active',
        updatedAt: Timestamp.now(),
        updatedBy: user.name
      });
      setEquipment(prev =>
        prev.map(e => e.id === eq.id ? { ...e, status: 'active' } : e)
      );
      setSelectedEquipment(null);
    } catch (err) {
      alert('Failed to reactivate. Please try again.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // condition 'good' | 'fair' | 'poor' → CSS modifier class (cond-good / cond-fair / cond-poor)
  const conditionClass = (c) => `cond-${c === 'good' || c === 'fair' || c === 'poor' ? c : 'fair'}`;

  const conditionLabel = (c) => ({
    good: '✅ Good', fair: '⚠️ Fair', poor: '🔴 Poor'
  }[c] || c);

  // ── Decommission Confirm Modal ────────────────────────────────────────────
  if (confirmDecommission) {
    return (
      <div className="eqm-modal-overlay">
        <div className="eqm-confirm-modal">
          <div className="eqm-confirm-icon">⚠️</div>
          <h2 className="eqm-confirm-title">Decommission Equipment?</h2>
          <p className="eqm-confirm-text">
            <strong>{confirmDecommission.serialNumber}</strong> at{' '}
            <strong>{confirmDecommission.outletName}</strong> will be marked as decommissioned.
            This can be undone later.
          </p>
          <div className="eqm-confirm-actions">
            <button
              className="eqm-btn cancel"
              onClick={() => setConfirmDecommission(null)}
            >
              Cancel
            </button>
            <button
              className="eqm-btn danger"
              onClick={() => handleDecommission(confirmDecommission)}
            >
              Decommission
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Detail / Edit Modal ───────────────────────────────────────────────────
  if (selectedEquipment) {
    const services = serviceHistory;
    const isDecommissioned = selectedEquipment.status === 'decommissioned';

    return (
      <div className="eqm-page">
        <header className="eqm-header">
          <button onClick={() => setSelectedEquipment(null)} className="back-button">← Back</button>
          <div>
            <h1 className="eqm-header-h1">
              {selectedEquipment.equipmentType === 'keg_pump' ? '☕' : '🍺'}{' '}
              {selectedEquipment.serialNumber}
            </h1>
            <p className="eqm-header-sub">{selectedEquipment.outletName}</p>
          </div>
        </header>

        <div className="eqm-scroll">

          {/* Status Banner */}
          {isDecommissioned && (
            <div className="eqm-decommission-banner">
              🚫 This equipment has been decommissioned
            </div>
          )}

          {/* Equipment Details Card */}
          <div className="eqm-detail-card">
            <div className="eqm-card-header-row">
              <h2 className="eqm-card-title">Equipment Details</h2>
              {!isDecommissioned && !editMode && (
                <button className="eqm-edit-btn" onClick={() => setEditMode(true)}>
                  ✏️ Edit
                </button>
              )}
              {editMode && (
                <div className="eqm-edit-actions">
                  <button className="eqm-edit-btn cancel" onClick={() => setEditMode(false)}>
                    Cancel
                  </button>
                  <button
                    className="eqm-edit-btn save"
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                  >
                    {savingEdit ? 'Saving...' : '💾 Save'}
                  </button>
                </div>
              )}
            </div>

            {editMode ? (
              <div className="eqm-edit-form">
                <div>
                  <label className="eqm-field-label">Serial Number</label>
                  <input
                    className="eqm-edit-input"
                    value={editForm.serialNumber}
                    onChange={e => setEditForm(p => ({ ...p, serialNumber: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="eqm-field-label">Outlet Name</label>
                  <input
                    className="eqm-edit-input"
                    value={editForm.outletName}
                    onChange={e => setEditForm(p => ({ ...p, outletName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="eqm-field-label">Installation Date</label>
                  <input
                    type="date"
                    className="eqm-edit-input"
                    value={editForm.installationDate}
                    onChange={e => setEditForm(p => ({ ...p, installationDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="eqm-field-label">Condition</label>
                  <div className="eqm-condition-row">
                    {['good', 'fair', 'poor'].map(c => (
                      <button
                        key={c}
                        className={`eqm-condition-pill ${conditionClass(c)} ${editForm.condition === c ? 'active' : ''}`}
                        onClick={() => setEditForm(p => ({ ...p, condition: c }))}
                      >
                        {conditionLabel(c)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="eqm-field-label">Notes</label>
                  <textarea
                    className="eqm-edit-input textarea"
                    rows={3}
                    value={editForm.notes}
                    onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                  />
                </div>
              </div>
            ) : (
              <div className="eqm-detail-grid">
                <div className="eqm-detail-item">
                  <span className="eqm-detail-label">Type</span>
                  <span className="eqm-detail-value">
                    {selectedEquipment.equipmentType === 'keg_pump' ? '☕ Keg Pump' : '🍺 Draft Machine'}
                  </span>
                </div>
                <div className="eqm-detail-item">
                  <span className="eqm-detail-label">Serial Number</span>
                  <span className="eqm-detail-value">{selectedEquipment.serialNumber}</span>
                </div>
                <div className="eqm-detail-item">
                  <span className="eqm-detail-label">Outlet</span>
                  <span className="eqm-detail-value">🏪 {selectedEquipment.outletName}</span>
                </div>
                <div className="eqm-detail-item">
                  <span className="eqm-detail-label">Installation Date</span>
                  <span className="eqm-detail-value">{formatDate(selectedEquipment.installationDate)}</span>
                </div>
                <div className="eqm-detail-item">
                  <span className="eqm-detail-label">Installed By</span>
                  <span className="eqm-detail-value">👤 {selectedEquipment.technicianName}</span>
                </div>
                <div className="eqm-detail-item">
                  <span className="eqm-detail-label">Condition</span>
                  <span className={`eqm-detail-value ${conditionClass(selectedEquipment.condition)}`}>
                    {conditionLabel(selectedEquipment.condition)}
                  </span>
                </div>
                <div className="eqm-detail-item">
                  <span className="eqm-detail-label">Registered On</span>
                  <span className="eqm-detail-value">{formatTimestamp(selectedEquipment.createdAt)}</span>
                </div>
                {selectedEquipment.notes && (
                  <div className="eqm-detail-item notes-item">
                    <span className="eqm-detail-label">Notes</span>
                    <span className="eqm-detail-value notes-value">
                      {selectedEquipment.notes}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Service History */}
          <div className="eqm-detail-card">
            <div className="eqm-card-header-row">
              <h2 className="eqm-card-title">Service History</h2>
              <span className="eqm-badge">{services.length} records</span>
            </div>
            {services.length === 0 ? (
              <div className="eqm-empty-inline">
                <p>No service records found for this equipment</p>
              </div>
            ) : (
              services.slice(0, 10).map(s => (
                <div key={s.id} className="eqm-service-item">
                  <div className="eqm-service-left">
                    <span className="eqm-service-icon">
                      {s.serviceType === 'emergency' ? '🚨' : '🔧'}
                    </span>
                    <div>
                      <div className="eqm-service-main">{s.technicianName}</div>
                      <div className="eqm-service-sub">
                        {s.faultDescription
                          ? s.faultDescription.slice(0, 60) + (s.faultDescription.length > 60 ? '...' : '')
                          : 'No description'}
                      </div>
                    </div>
                  </div>
                  <div className="eqm-service-right">
                    <span className={`eqm-status-pill ${s.status === 'completed' ? 'completed' : 'active'}`}>
                      {s.status === 'completed' ? 'Done' : 'Active'}
                    </span>
                    <span className="eqm-service-date">
                      {formatTimestamp(s.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Actions */}
          {!editMode && (
            <div className="eqm-action-row">
              {isDecommissioned ? (
                <button
                  className="eqm-action-btn reactivate"
                  onClick={() => handleReactivate(selectedEquipment)}
                >
                  ✅ Reactivate Equipment
                </button>
              ) : (
                <button
                  className="eqm-action-btn decommission"
                  onClick={() => setConfirmDecommission(selectedEquipment)}
                >
                  🚫 Decommission Equipment
                </button>
              )}
            </div>
          )}

        </div>
      </div>
    );
  }

  // ── Main List View ────────────────────────────────────────────────────────
  return (
    <div className="eqm-page">
      <header className="eqm-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div>
          <h1 className="eqm-header-h1">Equipment Manager</h1>
          <p className="eqm-header-sub">All registered equipment</p>
        </div>
        <button onClick={loadData} className="back-button eqm-refresh-btn">
          🔄
        </button>
      </header>

      {/* Summary Stats */}
      <div className="eqm-stats-row">
        <div className="eqm-stat-box">
          <span className="eqm-stat-num">{counts.all}</span>
          <span className="eqm-stat-lbl">Active</span>
        </div>
        <div className="eqm-stat-box">
          <span className="eqm-stat-num">{counts.keg_pump}</span>
          <span className="eqm-stat-lbl">Keg Pumps</span>
        </div>
        <div className="eqm-stat-box">
          <span className="eqm-stat-num">{counts.draft_machine}</span>
          <span className="eqm-stat-lbl">Draft Machines</span>
        </div>
        <div className="eqm-stat-box">
          <span className="eqm-stat-num decommissioned">{counts.decommissioned}</span>
          <span className="eqm-stat-lbl">Decommissioned</span>
        </div>
      </div>

      {/* Search */}
      <div className="eqm-search-wrap">
        <input
          className="eqm-search-input"
          placeholder="🔍  Search serial number, outlet, technician..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Filter Pills */}
      <div className="eqm-filter-row">
        {[
          { key: 'all', label: `All (${counts.all})` },
          { key: 'keg_pump', label: `☕ Keg Pump (${counts.keg_pump})` },
          { key: 'draft_machine', label: `🍺 Draft Machine (${counts.draft_machine})` },
          { key: 'decommissioned', label: `🚫 Decommissioned (${counts.decommissioned})` }
        ].map(f => (
          <button
            key={f.key}
            className={`eqm-filter-pill ${activeFilter === f.key ? 'active' : ''}`}
            onClick={() => setActiveFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Equipment List */}
      <div className="eqm-list">
        {loading ? (
          <div className="eqm-empty">
            <div className="eqm-empty-icon-sm">⏳</div>
            <p>Loading equipment...</p>
          </div>
        ) : filteredEquipment.length === 0 ? (
          <div className="eqm-empty">
            <div className="eqm-empty-icon-lg">📭</div>
            <p>No equipment found</p>
          </div>
        ) : (
          filteredEquipment.map(eq => {
            const isDecommissioned = eq.status === 'decommissioned';
            return (
              <div
                key={eq.id}
                className={`eqm-card ${isDecommissioned ? 'decommissioned' : ''}`}
                onClick={() => openDetail(eq)}
              >
                <div className="eqm-card-left">
                  <span className="eqm-card-icon">
                    {eq.equipmentType === 'keg_pump' ? '☕' : '🍺'}
                  </span>
                  <div>
                    <div className="eqm-card-serial">{eq.serialNumber}</div>
                    <div className="eqm-card-outlet">🏪 {eq.outletName}</div>
                    <div className="eqm-card-meta">
                      👤 {eq.technicianName} · 📅 {formatDate(eq.installationDate)}
                    </div>
                  </div>
                </div>
                <div className="eqm-card-right">
                  <span className={conditionClass(eq.condition)}>
                    {conditionLabel(eq.condition)}
                  </span>
                  {/* ✅ REMOVED: per-card service count. Showing it here meant
                      scanning the full service history once per equipment
                      card, on every render — at real fleet size (tens of
                      thousands of pumps) that's what actually risked
                      freezing this screen. Tap into an item to see its
                      real, accurate service history instead. */}
                  {isDecommissioned && (
                    <span className="eqm-decommissioned-tag">🚫 Decommissioned</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SupervisorEquipmentManager;
